import React, { FC, useState } from 'react';
import type { UploadFile, UploadProps } from 'antd';
import { App, Button, Tooltip, Upload } from 'antd';
import { faUpload, Icon } from '../Icon';

const { Dragger } = Upload;

interface DragUploadProps {
  uploadButtonText?: string;
  uploadText?: string;
  accept?: UploadProps['accept'];
  multiple?: UploadProps['multiple'];
  maxFileSize?: number;
  invalidMaxFileSizeMessage?: string;
  showUploadBtn?: boolean;
  showUploadList?: boolean;
  miniMode?: boolean;
  onFileChanged?: (files: UploadFile[]) => void;
  onFileSelected?: (file: File) => void;
  onUpload?: () => Promise<void>;
  onFinished?: () => void;
}

export const DragUpload: FC<DragUploadProps> = ({
  uploadButtonText,
  uploadText = 'Click or drag file to this area to upload',
  accept,
  multiple,
  maxFileSize,
  invalidMaxFileSizeMessage,
  showUploadBtn = true,
  showUploadList = true,
  miniMode,
  onFileChanged,
  onFileSelected,
  onUpload,
  onFinished,
}) => {
  const { message } = App.useApp();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onChange = (files: UploadFile[]) => {
    if (maxFileSize) {
      const invalidFiles = files.filter(
        (file) => !file.size || file.size > maxFileSize,
      );
      if (invalidFiles.length > 0) {
        const msg = invalidMaxFileSizeMessage || 'File is too large';
        message.error(msg);
        return false;
      }
    }
    setShowUpload(multiple || files.length > 0);
    const showFiles = multiple ? files : files.slice(-1);
    setFileList(showFiles);
    onFileChanged?.(showFiles);
    const selected = showFiles[showFiles.length - 1]?.originFileObj;
    if (selected) onFileSelected?.(selected);
    return false;
  };

  const onClickUpload = async (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!fileList?.length) return;
    setUploading(true);
    await onUpload?.();
    onChange([]);
    setUploading(false);
    onFinished?.();
  };

  return (
    <div>
      <Dragger
        accept={accept}
        multiple={multiple || false}
        beforeUpload={() => false}
        onChange={({ fileList: files }) => onChange(files)}
        fileList={fileList}
        showUploadList={showUploadList}
        className="custom-upload-wrapper"
      >
        <div>
          {miniMode ? (
            <Tooltip title={uploadText}>
              <div className="ant-upload-drag-icon">
                <Icon icon={faUpload} scale={2} />
              </div>
            </Tooltip>
          ) : (
            <>
              <div className="ant-upload-drag-icon">
                <Icon icon={faUpload} scale={2} />
              </div>
              <div className="ant-upload-text">{uploadText}</div>
            </>
          )}
          {showUploadBtn && showUpload ? (
            <Button
              type="primary"
              loading={uploading}
              disabled={uploading}
              onClick={onClickUpload}
              size={miniMode ? 'small' : undefined}
            >
              {uploading ? 'Uploading...' : uploadButtonText || 'Upload'}
            </Button>
          ) : null}
        </div>
      </Dragger>
    </div>
  );
};
